import React from "react";
import {
  CaseUpper,
  Mail,
  ChevronRight,
  ChevronDown,
  Folder,
  FolderOpen,
  Crown,
  Building,
  Edit,
  Trash2,
  GraduationCap,
  CreditCard,
  Phone,
  UserCheck,
  Filter,
  Plus,
  Download,
  Upload,
  IdCard,
  ShoppingCart,
  Calendar,
  XCircle,
  AlertCircle,
  Clock,
  CheckCircle,
  FileText,
  Check,
} from "lucide-react";
import { useState, useMemo, useEffect } from "react";
import TabPage, { TabItemProps } from "../../components/dynamic/Tab";
import { useNavigate, useSearchParams } from "react-router-dom";
import checkPermission from "../../utils/permission";
import { ApiService } from "../../services/api";
import Modal from "../../components/ui/Modal";
import { useAuth } from "../../contexts/AuthContext";
import BulkUserImportModal from "./components/BulkUserImportModal";
import { usePanels } from "../../contexts/PanelContext.jsx";
import { LIST_TOOLBAR_BTN, LIST_TOOLBAR_ICON, LIST_TOOLBAR_ROW } from "../../constants/listToolbarButtons";

interface Department {
  id: string;
  name: string;
  color?: string;
}

export default function EmployesPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { mainContentRightInsetActive } = usePanels();
  const [permissionsPanelDept, setPermissionsPanelDept] = useState<string | null>(null);

  const hasUserAddPermission = checkPermission("user_add");
  const hasUserEditPermission = checkPermission("user_update");
  const hasUserDeletePermission = checkPermission("user_delete");
  const hasDepartmentAddPermission = checkPermission("department_add");
  const hasDepartmentEditPermission = checkPermission("department_update");
  const hasDepartmentDeletePermission = checkPermission("department_delete");
  const [deletePanelDept, setDeletePanelDept] = useState<string | null>(null);
  const [expandedDepartments, setExpandedDepartments] = useState<Set<string>>(new Set());
  const [departmentData, setDepartmentData] = useState<any[]>([]);
  const [loadedDepartments, setLoadedDepartments] = useState<Set<string>>(new Set());
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<string | null>(null);
  const [deptFilterOpen, setDeptFilterOpen] = useState(true);
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<{ url: string; name: string } | null>(null);
  const [isBulkImportModalOpen, setIsBulkImportModalOpen] = useState(false);

  const isSuperAdmin = Boolean(user?.isSuperAdmin);

  useEffect(() => {
    try {
      if (typeof window !== "undefined") {
        const mobileMediaQuery = window.matchMedia("(max-width: 640px)");
        const updateViewportState = () => {
          const isMobile = mobileMediaQuery.matches;
          setIsMobileViewport(isMobile);
          if (isMobile) setDeptFilterOpen(false);
        };
        updateViewportState();
        mobileMediaQuery.addEventListener("change", updateViewportState);
        return () => {
          mobileMediaQuery.removeEventListener("change", updateViewportState);
        };
      }
    } catch (_) { }
  }, []);

  const closePanels = () => {
    setPermissionsPanelDept(null);
    setDeletePanelDept(null);
  };

  const toggleDepartment = async (departmentId: string | number) => {
    const idStr = String(departmentId);
    const newExpanded = new Set(expandedDepartments);

    if (newExpanded.has(idStr)) {
      newExpanded.delete(idStr);

      const closeChildren = (parentId: string) => {
        departmentData.forEach((dept) => {
          if (String(dept.parentId) === parentId) {
            newExpanded.delete(dept.id);
            closeChildren(dept.id);
          }
        });
      };
      closeChildren(idStr);
    } else {
      newExpanded.add(idStr);
      if (!loadedDepartments.has(idStr)) {
        await loadDepartments(idStr);
      }
    }
    setExpandedDepartments(newExpanded);
  };

  const buildDepartmentTree = (departments: any[], parentId: string | null = null): any[] => {
    return departments
      .filter((dept) => dept.parentId === parentId)
      .sort((a: any, b: any) =>
        (a.name || "").localeCompare(b.name || "", "tr", {
          sensitivity: "accent",
        })
      );
  };

  const getAllDescendantIds = (flatDepartments: any[], rootId: string): string[] => {
    const result: string[] = [];
    const rootStr = String(rootId);
    const children = flatDepartments.filter((d) => String(d.parentId) === rootStr);
    children.forEach((child) => {
      const childIdStr = String(child.id);
      result.push(childIdStr);
      result.push(...getAllDescendantIds(flatDepartments, childIdStr));
    });
    return result;
  };

  const getVisibleDepartments = (departments: any[], level: number = 0): any[] => {
    let result: any[] = [];

    departments.forEach((dept) => {
      result.push({ ...dept, level });

      if (expandedDepartments.has(dept.id)) {
        const children = departmentData.filter((d) => String(d.parentId) === String(dept.id));
        if (children.length > 0) {
          result = result.concat(getVisibleDepartments(children, level + 1));
        }
      }
    });

    return result;
  };

  const loadDepartments = async (parentId: string | null = null) => {
    try {
      const res: any = await ApiService.getAllDepartments(parentId);
      const list = (res as any)?.data || (res as any) || [];
      const activeOnly = list.filter((d: any) => !d?.deletedAt);

      if (parentId) {
        setDepartmentData((prev) => {
          const existingIds = new Set(prev.map((d) => d.id));
          const newDepartments = activeOnly.filter((d: any) => !existingIds.has(d.id));
          return [...prev, ...newDepartments];
        });
        setLoadedDepartments((prev) => new Set([...prev, parentId]));
      } else {
        setDepartmentData(activeOnly);
        setLoadedDepartments(new Set());
      }
    } catch (_) { }
  };

  useEffect(() => {
    const currentTab = searchParams.get("tab");
    if (currentTab !== "departments") {
      loadDepartments();
    }
  }, []);

  useEffect(() => {
    const updated = searchParams.get("updated");
    const created = searchParams.get("created");

    if (updated || created) {
      setRefreshTrigger((prev) => prev + 1);

      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete("updated");
      newSearchParams.delete("created");
      navigate(`/employees?${newSearchParams.toString()}`, { replace: true });
    }
  }, [searchParams, navigate]);

  useEffect(() => {
    const refreshData = () => {
      const currentTab = new URLSearchParams(window.location.search).get("tab");
      if (currentTab !== "departments") {
        loadDepartments();
      }
      setRefreshTrigger((prev) => prev + 1);
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        refreshData();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", refreshData);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", refreshData);
    };
  }, []);

  const renderDepartmentFilter = () => {
    const rootDepartments = buildDepartmentTree(departmentData.filter((d) => !d?.deletedAt));

    const renderNode = (node: any, level = 0) => {
      const hasChildren = node.hasChildren || false;
      const nodeId = String(node.id);
      const isExpanded = expandedDepartments.has(nodeId);
      const isSelected = selectedDepartmentId === nodeId;

      const children = departmentData.filter((d) => String(d.parentId) === nodeId);

      return (
        <div key={node.id}>
          <div
            className={`w-full flex items-center justify-between px-2 py-1.5 rounded-md text-sm ${isSelected ? "bg-blue-50 text-blue-700" : "text-gray-700"
              }`}
            style={{ paddingLeft: `${level * 12 + 8}px` }}>
            <div className="flex items-center gap-2 min-w-0">
              {hasChildren ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleDepartment(nodeId);
                  }}
                  className="text-gray-600 hover:text-gray-800 w-8 h-8 flex items-center justify-center rounded hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer z-10"
                  aria-label={isExpanded ? "Alt departmanları kapat" : "Alt departmanları aç"}
                  title={isExpanded ? "Alt departmanları kapat" : "Alt departmanları aç"}>
                  {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>
              ) : (
                <span className="w-8 h-8" />
              )}
              {hasChildren ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setSelectedDepartmentId(nodeId);
                    setRefreshTrigger((prev) => prev + 1);
                    if (isMobileViewport) setDeptFilterOpen(false);
                  }}
                  className={`truncate text-left ${isSelected ? "text-blue-700" : "text-gray-700 hover:bg-gray-100"} px-0.5 py-0.5 rounded`}
                  title="Bu departmandaki kullanıcıları filtrele">
                  {node.name}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setSelectedDepartmentId(nodeId);
                    setRefreshTrigger((prev) => prev + 1);
                    if (isMobileViewport) setDeptFilterOpen(false);
                  }}
                  className={`truncate text-left ${isSelected ? "text-blue-700" : "text-gray-700 hover:bg-gray-100"
                    } px-0.5 py-0.5 rounded`}>
                  {node.name}
                </button>
              )}
            </div>
          </div>
          {hasChildren && isExpanded && <div>{children.map((c: any) => renderNode(c, level + 1))}</div>}
        </div>
      );
    };

    return (
      <div className={isMobileViewport ? "w-full" : "pr-2 w-52"}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-600" />
            <span className="text-sm font-semibold text-gray-800">Filtreler</span>
          </div>
          <div className="flex items-center gap-1" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">Departman</label>
          <button
            onClick={() => {
              setSelectedDepartmentId(null);
              setRefreshTrigger((prev) => prev + 1);
              if (isMobileViewport) setDeptFilterOpen(false);
            }}
            className={`w-full px-2 py-1.5 rounded-md text-sm mb-1 text-left ${selectedDepartmentId === null ? "bg-blue-50 text-blue-700" : "text-gray-700 hover:bg-gray-100"
              }`}>
            Tümü
          </button>
          <div className={`space-y-1 max-h-[70vh] overflow-y-auto ${isMobileViewport ? "" : "pr-1"}`}>{rootDepartments.map((n) => renderNode(n))}</div>
        </div>
      </div>
    );
  };

  const getAvatar = (user: any) => {
    let avatar = "";
    if (user.firstName) avatar += user.firstName[0];
    if (user.lastName) avatar += user.lastName[0];
    return avatar;
  };

  const formatPhoneNumber = (phone: string) => {
    if (!phone) return phone;
    if (phone.startsWith("0")) return phone;
    return `0${phone}`;
  };

  const handleExportUsers = async () => {
    try {
      const blob = await ApiService.exportUsers(selectedDepartmentId || undefined);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `kullanicilar_${new Date().getTime()}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Excel export hatası:", error);
      alert("Kullanıcılar dışa aktarılırken bir hata oluştu.");
    }
  };

  const usersActions = useMemo(
    () => (
      <div className={LIST_TOOLBAR_ROW}>
        <button
          onClick={() => setDeptFilterOpen((prev) => !prev)}
          className={`${LIST_TOOLBAR_BTN} ${deptFilterOpen
            ? "bg-blue-600 text-white hover:bg-blue-700 border-blue-600"
            : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
            }`}
          title="Filtrele"
          aria-pressed={deptFilterOpen}>
          <Filter className={LIST_TOOLBAR_ICON} />
          <span>Filtrele</span>
        </button>
        <button
          onClick={handleExportUsers}
          className={`${LIST_TOOLBAR_BTN} bg-orange-600 text-white hover:bg-orange-700 border-orange-600`}
          title={
            selectedDepartmentId
              ? "Seçili departman ve alt departmanlarındaki kullanıcıları dışa aktar"
              : "Tüm kullanıcıları dışa aktar"
          }>
          <Download className={LIST_TOOLBAR_ICON} />
          <span>Dışa Aktar</span>
        </button>
        {isSuperAdmin && (
          <button
            onClick={() => setIsBulkImportModalOpen(true)}
            className={`${LIST_TOOLBAR_BTN} bg-indigo-600 text-white hover:bg-indigo-700 border-indigo-600`}
            title="Excel ile toplu kullanıcı ekle">
            <Upload className={LIST_TOOLBAR_ICON} />
            <span>Toplu Ekle</span>
          </button>
        )}
        {hasUserAddPermission && (
          <button
            onClick={() => navigate("/employees/users/new")}
            className={`${LIST_TOOLBAR_BTN} bg-green-600 text-white hover:bg-green-700 border-green-600`}>
            <Plus className={LIST_TOOLBAR_ICON} />
            <span>Yeni Kullanıcı</span>
          </button>
        )}
      </div>
    ),
    [hasUserAddPermission, navigate, deptFilterOpen, selectedDepartmentId, isSuperAdmin]
  );

  const usersTab: TabItemProps = useMemo(
    () => ({
      type: "grid",
      title: "Kullanıcılar",
      path: selectedDepartmentId ? `users?departmentId=${selectedDepartmentId}` : "users",
      actions: usersActions,
      isSearch: true,
      showToggle: true,
      leftArea: !isMobileViewport && deptFilterOpen ? renderDepartmentFilter : undefined,
      refreshKey: `users-${refreshTrigger}-${selectedDepartmentId || "all"}`,
      gridClassName:
        mainContentRightInsetActive
          ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 pb-8 items-stretch auto-rows-fr"
          : "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 pb-8 items-stretch auto-rows-fr",
      item: (user, viewMode = "list") => {
        return (
          <div
            key={user.id}
            className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow flex flex-col h-full mb-3">
            <div className="flex items-center gap-3 mb-3 relative">
              {(hasUserEditPermission || hasUserDeletePermission) && viewMode === "list" && (
                <div className="absolute top-1/2 right-0 -translate-y-1/2 hidden md:flex gap-1">
                  {hasUserEditPermission && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/employees/users/edit/${user.id}`);
                      }}
                      className="flex items-center justify-center w-[31px] h-[31px] text-blue-600 hover:text-blue-800 rounded-full hover:bg-blue-50 transition-colors"
                      title="Kullanıcıyı Düzenle">
                      <Edit className="w-[17px] h-[17px]" />
                    </button>
                  )}
                  {hasUserDeletePermission && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/employees/users/${user.id}/delete`);
                      }}
                      className="flex items-center justify-center w-[31px] h-[31px] text-red-600 hover:text-red-800 rounded-full hover:bg-red-50 transition-colors"
                      title="Kullanıcıyı Sil">
                      <Trash2 className="w-[17px] h-[17px]" />
                    </button>
                  )}
                </div>
              )}

              <div
                className={`w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center relative flex-shrink-0 overflow-hidden ${user.media?.url ? "cursor-pointer hover:opacity-80 transition-opacity" : ""
                  }`}
                onClick={(e) => {
                  if (user.media?.url) {
                    e.stopPropagation();
                    setSelectedImage({ url: user.media.url, name: `${user.firstName} ${user.lastName}` });
                    setIsImageModalOpen(true);
                  }
                }}>
                {user.media?.url ? (
                  <img src={user.media.url} alt={`${user.firstName} ${user.lastName}`} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-white font-semibold text-lg">{getAvatar(user)}</span>
                )}
              </div>

              <div className={`flex-1 min-w-0 ${viewMode === "list" ? "md:pr-16" : ""}`}>
                {viewMode === "grid" ? (
                  <div className="min-w-0">
                    <h3 className="font-medium text-gray-900 truncate">{user.firstName}</h3>
                    <h4 className="font-medium text-gray-900 truncate">{user.lastName}</h4>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 flex-wrap pr-2">
                    <h3 className="font-semibold text-gray-900 truncate text-[15px]">
                      {user.firstName} {user.lastName}
                    </h3>
                    {user.isSuperAdmin && (
                      <span
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200"
                        title="Süper Admin">
                        <Crown className="w-3 h-3 flex-shrink-0" />
                        <span>Süper Admin</span>
                      </span>
                    )}
                    {user.isBlock && (
                      <span
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200"
                        title="Engellenmiş">
                        <XCircle className="w-3 h-3 flex-shrink-0" />
                        <span>Engelli</span>
                      </span>
                    )}
                    {user.paymentStatus === 0 && (
                      <span
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200"
                        title="Ödeme Yapılmadı">
                        <AlertCircle className="w-3 h-3 flex-shrink-0" />
                        <span>Ödeme Yok</span>
                      </span>
                    )}
                    {user.paymentStatus === 1 && (
                      <span
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200"
                        title="Ödeme Bekliyor">
                        <Clock className="w-3 h-3 flex-shrink-0" />
                        <span>Bekliyor</span>
                      </span>
                    )}
                    {user.paymentStatus === 2 && !user.isBlock && (
                      <span
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200"
                        title="Ödeme Yapıldı">
                        <CheckCircle className="w-3 h-3 flex-shrink-0" />
                        <span>Ödendi</span>
                      </span>
                    )}
                    {user.paymentStatus === 3 && (
                      <span
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200"
                        title="Dilekçeli">
                        <FileText className="w-3 h-3 flex-shrink-0" />
                        <span>Dilekçeli</span>
                      </span>
                    )}
                    {!user.isUnlimitedLicense && user.licenseExpiryDate && (
                      <span
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-violet-50 text-violet-700 border border-violet-200"
                        title={`${Math.ceil(
                          (new Date(user.licenseExpiryDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
                        )} Gün Kaldı`}>
                        <Clock className="w-3 h-3 flex-shrink-0" />
                        <span>
                          {Math.ceil((new Date(user.licenseExpiryDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))} Gün Kaldı
                        </span>
                      </span>
                    )}
                  </div>
                )}

                {viewMode === "grid" && <div className="mt-1"></div>}

                {viewMode === "list" && (
                  <div className="mt-2.5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-3.5 gap-y-2">
                    {user.department?.name && (
                      <div className="flex items-center gap-1.5 min-w-0">
                        <Building className="w-3 h-3 text-gray-400 flex-shrink-0" />
                        <span className="text-xs text-gray-600 leading-5 truncate">Departman: {user.department.name}</span>
                      </div>
                    )}

                    {user.email && (
                      <div className="flex items-center gap-1.5 min-w-0">
                        <Mail className="w-3 h-3 text-gray-400 flex-shrink-0" />
                        <span className="text-xs text-gray-600 leading-5 flex items-center gap-1 min-w-0 w-full">
                          <span className="flex-shrink-0">E-posta:</span>
                          <a
                            href={`mailto:${user.email}`}
                            className="text-blue-600 hover:text-blue-800 hover:underline min-w-0 flex-1 truncate"
                            onClick={(e) => e.stopPropagation()}>
                            {user.email}
                          </a>
                        </span>
                        {user.isVerified && (
                          <span className="text-green-500 flex-shrink-0">
                            <Check className="w-3 h-3" />
                          </span>
                        )}
                      </div>
                    )}

                    {user.phone && (
                      <div className="flex items-center gap-1.5 min-w-0">
                        <Phone className="w-3 h-3 text-gray-400 flex-shrink-0" />
                        <span className="text-xs text-gray-600 leading-5 flex items-center gap-1 min-w-0">
                          <span className="flex-shrink-0">Telefon No:</span>
                          <a
                            href={`tel:${formatPhoneNumber(user.phone)}`}
                            className="text-blue-600 hover:text-blue-800 hover:underline truncate"
                            onClick={(e) => e.stopPropagation()}>
                            {formatPhoneNumber(user.phone)}
                          </a>
                        </span>
                      </div>
                    )}

                    {user.metadata?.schoolNumber && (
                      <div className="flex items-center gap-1.5 min-w-0">
                        <GraduationCap className="w-3 h-3 text-gray-400 flex-shrink-0" />
                        <span className="text-xs text-gray-600 leading-5 truncate">Okul No: {user.metadata.schoolNumber}</span>
                      </div>
                    )}

                    {user.metadata?.tcNumber && (
                      <div className="flex items-center gap-1.5 min-w-0">
                        <IdCard className="w-3 h-3 text-gray-400 flex-shrink-0" />
                        <span className="text-xs text-gray-600 leading-5 truncate">TC: {user.metadata.tcNumber}</span>
                      </div>
                    )}

                    {user.metadata?.parentPhone && (
                      <div className="flex items-center gap-1.5 min-w-0">
                        <Phone className="w-3 h-3 text-gray-400 flex-shrink-0" />
                        <span className="text-xs text-gray-600 leading-5 flex items-center gap-1 min-w-0">
                          <span className="flex-shrink-0">Veli Tel:</span>
                          <a
                            href={`tel:${formatPhoneNumber(user.metadata.parentPhone)}`}
                            className="text-blue-600 hover:text-blue-800 hover:underline truncate"
                            onClick={(e) => e.stopPropagation()}>
                            {formatPhoneNumber(user.metadata.parentPhone)}
                          </a>
                        </span>
                      </div>
                    )}

                    {user.metadata?.parentEmail && (
                      <div className="flex items-center gap-1.5 min-w-0">
                        <UserCheck className="w-3 h-3 text-gray-400 flex-shrink-0" />
                        <span className="text-xs text-gray-600 leading-5 flex items-center gap-1 min-w-0">
                          <span className="flex-shrink-0">Veli E-posta:</span>
                          <a
                            href={`mailto:${user.metadata.parentEmail}`}
                            className="text-blue-600 hover:text-blue-800 hover:underline truncate"
                            onClick={(e) => e.stopPropagation()}>
                            {user.metadata.parentEmail}
                          </a>
                        </span>
                      </div>
                    )}

                    {user.metadata?.orderCode && (
                      <div className="flex items-center gap-1.5 min-w-0">
                        <ShoppingCart className="w-3 h-3 text-gray-400 flex-shrink-0" />
                        <span className="text-xs text-gray-600 leading-5 truncate">Sipariş Kodu: {user.metadata.orderCode}</span>
                      </div>
                    )}

                    {user.metadata?.orderDate && (
                      <div className="flex items-center gap-1.5 min-w-0">
                        <Calendar className="w-3 h-3 text-gray-400 flex-shrink-0" />
                        <span className="text-xs text-gray-600 leading-5 truncate">
                          Sipariş Tarihi: {new Date(user.metadata.orderDate).toLocaleDateString("tr-TR")}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {(hasUserEditPermission || hasUserDeletePermission) && viewMode === "list" && (
              <div className="mt-3 pt-3 border-t border-gray-100 md:hidden">
                <div className="flex gap-2 justify-center">
                  {hasUserEditPermission && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/employees/users/edit/${user.id}`);
                      }}
                      className="flex items-center justify-center gap-2 px-3 py-2 text-blue-600 hover:text-blue-800 rounded-lg hover:bg-blue-50 transition-colors font-medium text-sm flex-1"
                      title="Kullanıcıyı Düzenle">
                      <Edit className="w-4 h-4" />
                      Düzenle
                    </button>
                  )}
                  {hasUserDeletePermission && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/employees/users/${user.id}/delete`);
                      }}
                      className="flex items-center justify-center gap-2 px-3 py-2 text-red-600 hover:text-red-800 rounded-lg hover:bg-red-50 transition-colors font-medium text-sm flex-1"
                      title="Kullanıcıyı Sil">
                      <Trash2 className="w-4 h-4" />
                      Sil
                    </button>
                  )}
                </div>
              </div>
            )}

            {viewMode === "grid" && (
              <div className="mt-2 space-y-1 ml-15">
                {user.department?.name && (
                  <div className="flex items-center gap-1">
                    <Building className="w-3 h-3 text-gray-400" />
                    <span className="text-xs text-gray-600 truncate">Departman: {user.department.name}</span>
                  </div>
                )}

                {user.email && (
                  <div className="flex items-center gap-1 max-w-48">
                    <Mail className="w-3 h-3 text-gray-400 flex-shrink-0" />
                    <span className="text-xs text-gray-600 flex-shrink-0">E-posta:</span>
                    <a
                      href={`mailto:${user.email}`}
                      className="text-xs text-blue-600 hover:text-blue-800 hover:underline truncate min-w-0"
                      onClick={(e) => e.stopPropagation()}>
                      {user.email}
                    </a>
                    {user.isVerified && (
                      <span className="text-green-500 flex-shrink-0">
                        <Check className="w-3 h-3" />
                      </span>
                    )}
                  </div>
                )}

                {user.phone && (
                  <div className="flex items-center gap-1 max-w-48">
                    <Phone className="w-3 h-3 text-gray-400 flex-shrink-0" />
                    <span className="text-xs text-gray-600 flex-shrink-0">Telefon No:</span>
                    <a
                      href={`tel:${formatPhoneNumber(user.phone)}`}
                      className="text-xs text-blue-600 hover:text-blue-800 hover:underline truncate min-w-0"
                      onClick={(e) => e.stopPropagation()}>
                      {formatPhoneNumber(user.phone)}
                    </a>
                  </div>
                )}

                {user.metadata?.schoolNumber && (
                  <div className="flex items-center gap-1">
                    <GraduationCap className="w-3 h-3 text-gray-400" />
                    <span className="text-xs text-gray-600">Okul No: {user.metadata.schoolNumber}</span>
                  </div>
                )}

                {user.metadata?.tcNumber && (
                  <div className="flex items-center gap-1">
                    <IdCard className="w-3 h-3 text-gray-400" />
                    <span className="text-xs text-gray-600">TC: {user.metadata.tcNumber}</span>
                  </div>
                )}

                {user.metadata?.orderCode && (
                  <div className="flex items-center gap-1">
                    <ShoppingCart className="w-3 h-3 text-gray-400" />
                    <span className="text-xs text-gray-600">Sipariş Kodu: {user.metadata.orderCode}</span>
                  </div>
                )}

                {user.metadata?.orderDate && (
                  <div className="flex items-center gap-1">
                    <Calendar className="w-3 h-3 text-gray-400" />
                    <span className="text-xs text-gray-600">
                      Sipariş Tarihi: {new Date(user.metadata.orderDate).toLocaleDateString("tr-TR")}
                    </span>
                  </div>
                )}
              </div>
            )}

            {viewMode === "grid" && (
              <div className="mt-auto">
                <div className="mb-4 flex items-center gap-2 flex-wrap">
                  {user.isSuperAdmin && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-sm max-w-full min-w-0">
                      <Crown className="w-3 h-3 flex-shrink-0" />
                      <span className="inline md:hidden lg:inline truncate">Süper Admin</span>
                    </span>
                  )}
                  {user.isBlock && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-red-500 to-red-600 text-white shadow-sm max-w-full min-w-0">
                      <XCircle className="w-3 h-3 flex-shrink-0" />
                      <span className="inline md:hidden lg:inline truncate">Engellenmiş</span>
                    </span>
                  )}
                  {user.paymentStatus === 0 && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-red-500 to-red-600 text-white shadow-sm max-w-full min-w-0">
                      <AlertCircle className="w-3 h-3 flex-shrink-0" />
                      <span className="inline md:hidden lg:inline truncate">Ödeme Yapılmadı</span>
                    </span>
                  )}
                  {user.paymentStatus === 1 && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-yellow-500 to-yellow-600 text-white shadow-sm max-w-full min-w-0">
                      <Clock className="w-3 h-3 flex-shrink-0" />
                      <span className="inline md:hidden lg:inline truncate">Ödeme Bekliyor</span>
                    </span>
                  )}
                  {user.paymentStatus === 2 && !user.isBlock && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-green-500 to-green-600 text-white shadow-sm max-w-full min-w-0">
                      <CheckCircle className="w-3 h-3 flex-shrink-0" />
                      <span className="inline md:hidden lg:inline truncate">Ödeme Yapıldı</span>
                    </span>
                  )}
                  {user.paymentStatus === 3 && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-sm max-w-full min-w-0">
                      <FileText className="w-3 h-3 flex-shrink-0" />
                      <span className="inline md:hidden lg:inline truncate">Dilekçeli</span>
                    </span>
                  )}
                  {!user.isUnlimitedLicense && user.licenseExpiryDate && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-sm max-w-full min-w-0">
                      <Clock className="w-3 h-3 flex-shrink-0" />
                      <span className="inline md:hidden lg:inline truncate">
                        {Math.ceil((new Date(user.licenseExpiryDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))} Gün
                      </span>
                    </span>
                  )}
                </div>

                {(hasUserEditPermission || hasUserDeletePermission) && (
                  <div className="pt-3 border-t border-gray-100">
                    <div className="grid grid-cols-2 gap-2 w-full">
                      {hasUserEditPermission && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/employees/users/edit/${user.id}`);
                          }}
                          className="flex items-center justify-center gap-1.5 px-2 py-2 text-blue-600 hover:text-blue-800 rounded-lg hover:bg-blue-50 transition-colors font-medium text-sm min-w-0 whitespace-nowrap"
                          title="Kullanıcıyı Düzenle">
                          <Edit className="w-4 h-4 flex-shrink-0" />
                          <span>Düzenle</span>
                        </button>
                      )}
                      {hasUserDeletePermission && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/employees/users/${user.id}/delete`);
                          }}
                          className="flex items-center justify-center gap-1.5 px-2 py-2 text-red-600 hover:text-red-800 rounded-lg hover:bg-red-50 transition-colors font-medium text-sm min-w-0 whitespace-nowrap"
                          title="Kullanıcıyı Sil">
                          <Trash2 className="w-4 h-4 flex-shrink-0" />
                          <span>Sil</span>
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      },
    }),
    [
      usersActions,
      selectedDepartmentId,
      departmentData,
      expandedDepartments,
      leftPanelCollapsed,
      deptFilterOpen,
      refreshTrigger,
      hasUserEditPermission,
      hasUserDeletePermission,
      mainContentRightInsetActive,
    ]
  );

  const departmentsActions = useMemo(
    () => (
      <div className={LIST_TOOLBAR_ROW}>
        {hasDepartmentAddPermission && (
          <button
            onClick={() => navigate("/employees/departments/new")}
            className={`${LIST_TOOLBAR_BTN} !h-[42px] !rounded-lg !px-3 bg-green-600 text-white hover:bg-green-700 border-green-600`}>
            <Plus className={LIST_TOOLBAR_ICON} />
            <span>Yeni Departman</span>
          </button>
        )}
      </div>
    ),
    [navigate, hasDepartmentAddPermission]
  );

  const departmentsTab: TabItemProps = useMemo(
    () => ({
      type: "list",
      title: "Departmanlar",

      path: "departments",
      fetcher: async (page: number, searchTerm?: string) => {
        if (searchTerm && searchTerm.trim()) {
          const response = await ApiService.searchDepartments({
            page,
            limit: 20,
            sortBy: "name",
            sortOrder: "ASC",
            search: searchTerm.trim(),
          });
          return response;
        } else {
          const response = await ApiService.getDepartments(page, 20);
          return response;
        }
      },
      actions: departmentsActions,
      isSearch: true,
      showToggle: false,
      dataProcessor: (rawData: any[], searchTerm?: string) => {
        let activeOnly = (rawData || []).filter((d: any) => !d?.deletedAt);

        try {
          const locallyDeleted = JSON.parse(localStorage.getItem("deletedDepartmentIds") || "[]") as string[];
          if (Array.isArray(locallyDeleted) && locallyDeleted.length > 0) {
            const deletedSet = new Set(locallyDeleted);
            activeOnly = activeOnly.filter((d: any) => !deletedSet.has(d?.id));
          }
        } catch (_) { }

        if (searchTerm && searchTerm.trim()) {
          return activeOnly.map((d: any) => ({ ...d, isSearchMode: true }));
        }

        const activeOnlyIds = new Set((activeOnly || []).map((d: any) => String(d?.id)));
        const onlyChildren = (departmentData || []).filter((d: any) => d?.parentId !== null && !activeOnlyIds.has(String(d?.id)));
        const merged = [...activeOnly, ...onlyChildren];

        const tree = buildDepartmentTree(merged);
        return getVisibleDepartments(tree);
      },
      item: (department, viewMode = "list") => {
        const hasChildren = department.hasChildren || false;
        const isExpanded = expandedDepartments.has(department.id);
        const level = department.level || 0;
        const isSearchMode = department.isSearchMode || false;

        return (
          <React.Fragment key={department.id}>
            <div
              className={`flex items-stretch gap-2 mb-2 ${!isSearchMode && level > 0 ? "animate-[department-child-enter_220ms_ease-out]" : ""}`}
              style={{
                marginLeft: isSearchMode ? "0px" : `${Math.min(level * 12, 36)}px`,
              }}>
              {!isSearchMode && level > 0 && <div className="self-start mt-5 h-5 w-4 border-l border-b border-gray-300 rounded-bl-sm" />}
              <div className="bg-white border border-gray-200 rounded-lg hover:shadow-md transition-shadow flex-1">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between p-3 sm:px-4 sm:py-3 gap-3">
                  <div className="flex items-start gap-2.5 flex-1 min-w-0">
                  {!isSearchMode && hasChildren && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleDepartment(department.id);
                      }}
                      className="flex h-9 w-7 flex-shrink-0 items-center justify-center rounded text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700 sm:h-10">
                      <ChevronRight
                        className={`w-4 h-4 transition-transform duration-200 ease-out ${isExpanded ? "rotate-90" : "rotate-0"}`}
                      />
                    </button>
                  )}
                  {!isSearchMode && !hasChildren && <div className="h-9 w-7 flex-shrink-0 sm:h-10" />}

                  <div
                    className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center relative flex-shrink-0"
                    style={{
                      backgroundColor: department.color || "#F97316",
                    }}>
                    {hasChildren ? (
                      isExpanded ? (
                        <FolderOpen className="w-5 h-5 text-white" />
                      ) : (
                        <Folder className="w-5 h-5 text-white" />
                      )
                    ) : (
                      <Folder className="w-5 h-5 text-white" />
                    )}
                  </div>

                  <div className="flex-1 min-w-0 pt-0.5 space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-medium text-gray-900 text-sm sm:text-base leading-tight break-words">{department.name}</h3>
                      {hasChildren && (
                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full whitespace-nowrap">
                          {department.childrenCount} Alt Departman
                        </span>
                      )}
                    </div>
                    <p className="text-xs sm:text-sm text-gray-600 break-words min-h-[20px]">
                      {department.description || "-"}
                    </p>

                    {department.parent && (
                      <div className="flex items-start gap-1 mt-1 flex-wrap">
                        <CaseUpper className="w-3 h-3 text-gray-400 mt-0.5" />
                        <span className="text-xs text-gray-600 break-words">Üst Departman: {department.parent.name}</span>
                      </div>
                    )}
                  </div>
                </div>

                  {(hasDepartmentEditPermission || hasDepartmentDeletePermission) && (
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 mt-1 lg:mt-0 w-full lg:w-auto lg:pl-3 lg:border-l lg:border-gray-100">
                    {hasDepartmentEditPermission && (
                      <button
                        onClick={() => navigate(`/employees/departments/edit/${department.id}`)}
                        className="w-full sm:w-auto text-blue-600 hover:text-blue-800 text-sm font-medium px-3 py-2 rounded-md hover:bg-blue-50 flex items-center justify-center gap-1 min-h-[38px] border border-blue-100/70">
                        <Edit className="w-3 h-3" />
                        <span className="hidden sm:inline">Düzenle</span>
                        <span className="sm:hidden">Düzenle</span>
                      </button>
                    )}
                    {hasDepartmentEditPermission && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          navigate(`/employees/departments/${department.id}/permissions`);
                        }}
                        className="w-full sm:w-auto text-green-600 hover:text-green-800 text-sm font-medium px-3 py-2 rounded-md hover:bg-green-50 flex items-center justify-center gap-1 min-h-[38px] border border-green-100/70">
                        <UserCheck className="w-3 h-3" />
                        <span className="hidden sm:inline">İzinler</span>
                        <span className="sm:hidden">İzinler</span>
                      </button>
                    )}
                    {hasDepartmentDeletePermission && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          navigate(`/employees/departments/${department.id}/delete`);
                        }}
                        className="w-full sm:w-auto text-red-600 hover:text-red-800 text-sm font-medium px-3 py-2 rounded-md hover:bg-red-50 flex items-center justify-center gap-1 min-h-[38px] border border-red-100/70">
                        <Trash2 className="w-3 h-3" />
                        <span className="hidden sm:inline">Sil</span>
                        <span className="sm:hidden">Sil</span>
                      </button>
                    )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </React.Fragment>
        );
      },
    }),
    [departmentsActions, expandedDepartments, departmentData, hasDepartmentEditPermission, hasDepartmentDeletePermission]
  );

  const tabs = useMemo(() => [usersTab, departmentsTab], [usersTab, departmentsTab]);
  const activeTab = searchParams.get("tab") || "users";

  return (
    <>
      <style>
        {`
          @keyframes department-child-enter {
            from { opacity: 0; transform: translateY(-3px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}
      </style>
      <TabPage title="Üyeler" tabs={tabs} />
      {isMobileViewport && activeTab === "users" && deptFilterOpen && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-40 bg-black/20"
            onClick={() => setDeptFilterOpen(false)}
            aria-label="Filtre panelini kapat"
          />
          <div className="fixed left-3 right-3 top-[188px] z-50 max-h-[65vh] overflow-y-auto rounded-xl border border-gray-200 bg-white p-3 shadow-xl">
            {renderDepartmentFilter()}
          </div>
        </>
      )}
      <Modal
        isOpen={isImageModalOpen}
        onClose={() => {
          setIsImageModalOpen(false);
          setSelectedImage(null);
        }}
        title={selectedImage?.name}
        size="lg">
        {selectedImage && (
          <div className="flex items-center justify-center">
            <img src={selectedImage.url} alt={selectedImage.name} className="max-w-full max-h-[70vh] object-contain rounded-lg" />
          </div>
        )}
      </Modal>
      <BulkUserImportModal
        isOpen={isBulkImportModalOpen}
        onClose={() => setIsBulkImportModalOpen(false)}
        initialDepartmentId={selectedDepartmentId}
        onCompleted={() => setRefreshTrigger((prev) => prev + 1)}
      />
    </>
  );
}
